import React, { useState, useRef, KeyboardEvent } from 'react'
import {
  Box,
  HStack,
  Input,
  Text,
  Icon,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'

interface ChipInputProps {
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  helperText?: string
  /**
   * Optional validation function. Return error message or null if valid.
   */
  validate?: (value: string) => string | null
  /**
   * Color palette for chips
   */
  colorPalette?: 'brand' | 'blue' | 'green' | 'purple' | 'gray'
  /**
   * Maximum number of items allowed
   */
  maxItems?: number
}

export const ChipInput: React.FC<ChipInputProps> = ({
  value,
  onChange,
  placeholder = 'Type and press Enter...',
  helperText = 'Press Enter to add',
  validate,
  colorPalette = 'brand',
  maxItems
}) => {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const addItem = (item: string) => {
    const trimmedItem = item.trim()
    
    if (!trimmedItem) return

    // Check max items
    if (maxItems && value.length >= maxItems) {
      setError(`Maximum ${maxItems} items allowed`)
      return
    }

    // Run custom validation if provided
    if (validate) {
      const validationError = validate(trimmedItem)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    // Check for duplicates (case-insensitive)
    if (value.some(v => v.toLowerCase() === trimmedItem.toLowerCase())) {
      setError('This item is already added')
      return
    }

    setError(null)
    onChange([...value, trimmedItem])
    setInputValue('')
  }

  const removeItem = (itemToRemove: string) => {
    onChange(value.filter(item => item !== itemToRemove))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addItem(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last item when backspace is pressed on empty input
      removeItem(value[value.length - 1])
    }
  }

  const handleBlur = () => {
    // Add item on blur if there's content
    if (inputValue.trim()) {
      addItem(inputValue)
    }
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor={error ? 'red.300' : 'gray.200'}
        borderRadius="md"
        p={2}
        bg="white"
        cursor="text"
        onClick={handleContainerClick}
        _focusWithin={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
        transition="all 0.2s"
        minH="42px"
      >
        <Wrap gap={2} align="center">
          {value.map((item, index) => (
            <WrapItem key={`${item}-${index}`}>
              <HStack
                bg={`${colorPalette}.50`}
                color={`${colorPalette}.700`}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                gap={1}
              >
                <Text>{item}</Text>
                <Box
                  as="button"
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    removeItem(item)
                  }}
                  cursor="pointer"
                  _hover={{ color: `${colorPalette}.900` }}
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={FiX} boxSize={3} />
                </Box>
              </HStack>
            </WrapItem>
          ))}
          <WrapItem flex={1} minW="150px">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                setError(null)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={value.length === 0 ? placeholder : 'Add another...'}
              variant="unstyled"
              size="sm"
              fontSize="sm"
              _placeholder={{ color: 'gray.400' }}
            />
          </WrapItem>
        </Wrap>
      </Box>
      {error ? (
        <Text fontSize="xs" color="red.500" mt={1}>
          {error}
        </Text>
      ) : helperText ? (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {helperText}
        </Text>
      ) : null}
    </Box>
  )
}


